import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    QueryList,
    SimpleChanges,
    ViewChildren,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EditorSocketHelperService } from '@service/editor-socket-helper.service';
import { MimicFolderService } from '@service/mimic-folder.service';
import { Mimic } from 'icreate/common/models/mimic';
import { RootItem, RootItemType } from 'icreate/common/models/mimic-folder';
import { Vessel } from 'icreate/common/models/vessel';
import { Subject, takeUntil } from 'rxjs';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

@Component({
    selector: 'app-vessel-mimics-preview',
    templateUrl: './vessel-mimics-preview.component.html',
    styleUrl: './vessel-mimics-preview.component.css',
    standalone: false,
})
export class VesselMimicsPreviewComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() vessel: Vessel;
    @ViewChildren('svgHost') svgHosts!: QueryList<ElementRef<SVGSVGElement>>;

    rootList: RootItem[] = [];
    mimicList: (Mimic & { folderName: string })[] = [];

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private mimicFolderService: MimicFolderService,
        private editorSocketHelper: EditorSocketHelperService,
        private translateService: TranslateService,
        private router: Router,
        private cdr: ChangeDetectorRef
    ) {}

    @HostListener('window:resize', ['$event'])
    onResize(event: Event) {
        this.loadMimics();
    }

    ngAfterViewInit(): void {
        this.editorSocketHelper.rootList$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(list => {
            this.rootList = list;
        });
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes.vessel && this.vessel) {
            this.mimicList = [];
            this.rootList = await this.mimicFolderService.getByVesselId(this.vessel.id);
            this.loadMimics();
        }
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    private loadMimics() {
        const mimics = [];
        this.rootList.forEach(item => {
            if (item.type == RootItemType.mimic) mimics.push(item.mimic);
            else if (item.type == RootItemType.folder) {
                mimics.push(
                    ...item.folder.mimicList.map((mimic: Mimic) => {
                        const mimicWithFolder = mimic as Mimic & { folderName: string };
                        mimicWithFolder.folderName = item.folder.name;
                        return mimicWithFolder;
                    })
                );
            }
        });
        this.mimicList = mimics;

        this.cdr.detectChanges();
        mimics.forEach((mimic, index) => this.loadHmi(mimic, index));
    }

    getFormattedDate(mimic: Mimic & { folderName: string }) {
        const updatedString = this.translateService.instant('dlg.updated');
        const passedTime = dayjs(mimic.updatedAt)?.fromNow();
        return updatedString + ' ' + passedTime;
    }

    loadHmi(mimic: Mimic & { folderName: string }, index: number) {
        if (!mimic.svgcontent) return;
        if (!this.svgHosts) return;

        const svgHost = this.svgHosts.toArray()[index];
        if (!svgHost) return;

        const hostEl = svgHost.nativeElement as any;
        hostEl.innerHTML = '';
        hostEl.style.width = '';
        hostEl.style.height = '';

        const newDoc = this.text2xml(mimic.svgcontent);
        const svg = document.adoptNode(newDoc.documentElement) as unknown as SVGSVGElement;
        hostEl.append(svg);

        svg.style.background = mimic.profile.bkcolor;
        svg.style.overflow = 'hidden';
        svg.style.borderRadius = '4px 4px 0px 0px';

        let vbX = 0,
            vbY = 0,
            origW: number,
            origH: number;
        const vb = svg.getAttribute('viewBox');

        if (vb) {
            [vbX, vbY, origW, origH] = vb
                .trim()
                .split(/[\s,]+/)
                .map(Number);
        } else {
            origW = parseFloat(svg.getAttribute('width') ?? '') || 1704;
            origH = parseFloat(svg.getAttribute('height') ?? '') || 864;
        }

        svg.setAttribute('viewBox', `${vbX} ${vbY} ${origW} ${origH}`);
        svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

        const hostWidth = hostEl.getBoundingClientRect().width;
        const hostHeight = hostWidth * (origH / origW);
        hostEl.style.width = `${hostWidth}px`;
        hostEl.style.height = `${hostHeight}px`;

        svg.setAttribute('width', '100%');
        svg.setAttribute('height', '100%');

        this.cdr.detectChanges();
    }

    selectMimic(mimic: Mimic & { folderName: string }) {
        this.router.navigateByUrl(`editor/${this.vessel.id}`, {
            state: { selectedMimic: mimic.id },
        });
    }

    get sortedMimics() {
        return this.mimicList;
    }

    get numberOfMimics() {
        return this.mimicList?.length;
    }

    private text2xml(sXML) {
        if (sXML.includes('<svg:svg')) {
            sXML = sXML.replace(/<(\/?)svg:/g, '<$1').replace('xmlns:svg', 'xmlns');
        }
        let out;
        let dXML;
        try {
            dXML = new DOMParser();
            dXML.async = false;
        } catch (e) {
            throw new Error('XML Parser could not be instantiated');
        }
        try {
            out = dXML.parseFromString(sXML, 'text/xml');
        } catch (e2) {
            throw new Error('Error parsing XML string');
        }
        return out;
    }
}
